import type { ComponentType } from 'react';
import { useMemo } from 'react';
import type {
  ImageValue,
  MulticolorValueType
} from '@elfsight/configurator-contracts';
import { useSettings } from '@wu/widget-core';
import { isStandalone } from '../../../constants';
import { useChannels } from '../../channels';
import { useChat } from '../../chat';
import { getInvertedColor } from '../../helpers';

const ICON_SIZE = 24;
const BUTTON_FONT_SIZE = 16;
const LINE_HEIGHT = 1.33;
const STANDALONE_WIDTH = 'auto';
const STANDALONE_MAX_WIDTH = '100%';
const STANDARD_WIDTH = 'calc(25% - 6px)';
const STANDARD_HEIGHT_BUTTON = 48;
const BORDER_RADIUS_STANDARD = 16;
const PADDING = STANDARD_HEIGHT_BUTTON / 2;
const PADDING_WITH_ICON = (STANDARD_HEIGHT_BUTTON - ICON_SIZE) / 2;
const PADDING_ONLY_BY_TEXT =
  (STANDARD_HEIGHT_BUTTON - BUTTON_FONT_SIZE * LINE_HEIGHT) / 2;

const calculateWidth = (buttonsLength: number) => {
  if (isStandalone) {
    return STANDALONE_WIDTH;
  }

  return buttonsLength > 6 ? STANDARD_WIDTH : `${100 / buttonsLength}%`;
};

const calculateVerticalPadding = (
  hasText: boolean,
  isIconVisible: boolean,
  isHideButtonText: boolean
) => {
  if (isIconVisible) {
    return PADDING_WITH_ICON;
  }

  return hasText && !isHideButtonText ? PADDING_ONLY_BY_TEXT : PADDING;
};

type UseButtonStyles = {
  icon: ComponentType | ImageValue;
  color?: string;
  title?: string;
};
export const useButtonStyles = ({
  icon,
  color: defaultButtonColor,
  title: defaultButtonText
}: UseButtonStyles) => {
  const {
    targetChatButton: { appTitle: appTitleFromContext }
  } = useChat();
  const {
    chatWallpaperMulticolor,
    buttonsColor,
    buttonColor, // @TODO add absent type
    buttonBorderRadius,
    buttonIconVisible,
    buttonText
  } = useSettings();
  const channels = useChannels();
  const channelsLength = channels.length;
  const isIconVisible = buttonIconVisible && !!icon;
  const isHideButtonText = channelsLength > 2;
  const buttonColorSetting = isStandalone ? buttonColor : buttonsColor;

  const color = useMemo(
    () =>
      ({
        custom: buttonColorSetting?.color,
        native: defaultButtonColor
      })[buttonColorSetting?.type as MulticolorValueType],
    [buttonColorSetting, defaultButtonColor]
  );

  const fontColor = getInvertedColor({ color });

  const text = isStandalone ? buttonText : defaultButtonText;
  const hasText = !!text?.trim();
  const verticalPadding = calculateVerticalPadding(
    hasText,
    isIconVisible,
    isHideButtonText
  );
  const horizontalPadding = channelsLength === 1 ? 27 : 12;
  const iconOnly = buttonIconVisible && (isHideButtonText || !hasText);
  const iconLeft = isIconVisible && icon;
  const borderSpacing = appTitleFromContext ? '20px 20px 0' : '20px';
  const borderRadius = isStandalone
    ? buttonBorderRadius
    : BORDER_RADIUS_STANDARD;

  return {
    color,
    font: {
      fontSize: BUTTON_FONT_SIZE,
      fontWeight: 'bold'
    },
    borderWidth: 0,
    text,
    fontColor,
    iconOnly,
    iconLeft,
    horizontalPadding,
    verticalPadding,
    borderRadius,
    width: calculateWidth(channelsLength),
    style: {
      padding: 0,
      margin: isStandalone && borderSpacing,
      maxWidth: isStandalone && STANDALONE_MAX_WIDTH,
      boxShadow:
        chatWallpaperMulticolor?.type === 'native' &&
        '0 1px 0 0 rgba(0, 0, 0, 0.25)'
    }
  };
};
